import React from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { EyeIcon } from 'lucide-react';
import type { ConfigOptionRecord } from '../services/smr-admin';

interface Props {
    data: ConfigOptionRecord[];
    isLoading?: boolean;
}

export const ConfigOptionDataTable: React.FC<Props> = ({ data, isLoading = false }) => {
    const navigate = useNavigate();

    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading config options..." />;
    }

    const columns: ColumnDef<ConfigOptionRecord>[] = [
        {
            accessorKey: 'key_name',
            header: 'Key Name',
            cell: (info) => <span className="font-mono text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'data_type',
            header: 'Data Type',
            cell: (info) => <Badge size="xs" variant="info">{info.getValue() as string}</Badge>,
        },
        {
            accessorKey: 'is_required',
            header: 'Required',
            cell: (info) => <span className="text-sm">{(info.getValue() as boolean) ? 'Yes' : 'No'}</span>,
        },
        {
            accessorKey: 'created_at',
            header: 'Created',
            cell: (info) => <span className="text-sm">{new Date(info.getValue() as string).toLocaleDateString()}</span>,
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex space-x-2">
                    <Tooltip content="View config option">
                        <TextLink
                            onClick={() => navigate(`/smr-admin/config-options/${info.row.original.id}`)}
                            color="primary"
                            startIcon={<EyeIcon size={20} />}
                        />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return <Table<ConfigOptionRecord> data={data} columns={columns} enableSorting={false} />;
};
